import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import MatchRow from "@/components/MatchRow";
import MyBets from "@/components/MyBets";
import BetSlipDock from "@/components/BetSlipDock";
import { Wallet, UserCircle, KeyRound, Radio } from "lucide-react";

export default function ClientDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [pick, setPick] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    const load = () =>
      api.get("/cricket/matches").then((r) => setMatches(r.data.items.filter((m) => m.status !== "completed" && m.book?.length)));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!user) return null;
  return (
    <div className="min-h-screen bg-[#09090D] text-zinc-100">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10">
        <div className="text-xs uppercase font-display tracking-widest text-amber-400">Client Dugout</div>
        <h1 className="font-display text-4xl font-black uppercase gold-text mt-1">Welcome, {user.display_name}</h1>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          <Card className="bg-zinc-950 border-amber-500/20" data-testid="client-profile-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-amber-400 font-display uppercase tracking-widest text-xs">
                <UserCircle className="h-4 w-4" /> Profile
              </div>
              <div className="mt-3 text-sm text-zinc-300">{user.email}</div>
              <div className="text-xs text-zinc-500 mt-1">Role · {user.role}</div>
              <Button variant="outline" className="mt-5 border-amber-500/40 text-amber-300 hover:bg-amber-400 hover:text-black" onClick={() => nav("/change-password")} data-testid="change-password-btn">
                <KeyRound className="h-3 w-3 mr-2" /> Change password
              </Button>
            </CardContent>
          </Card>
          <Card className="bg-zinc-950 border-amber-500/20" data-testid="client-credits-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-amber-400 font-display uppercase tracking-widest text-xs">
                <Wallet className="h-4 w-4" /> Virtual Credits
              </div>
              <div className="mt-3 font-mono-stat text-5xl gold-text">{(user.credits || 0).toFixed(2)}</div>
              <Badge className="mt-3 bg-amber-500/15 text-amber-300 border border-amber-500/40">Non-redeemable</Badge>
              <div className="text-[11px] text-zinc-500 mt-3">
                Practice-only balance. No cash value, no withdrawals, no transfers.
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-950 border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-amber-400 font-display uppercase tracking-widest text-xs">
                <Radio className="h-4 w-4" /> Shortcuts
              </div>
              <div className="mt-5 flex flex-col gap-2">
                <Button onClick={() => nav("/?tab=live")} variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-400 hover:text-black">Live Matches</Button>
                <Button onClick={() => nav("/?tab=upcoming")} variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-400 hover:text-black">Upcoming Fixtures</Button>
                <Button onClick={() => nav("/?tab=completed")} variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-400 hover:text-black">Results</Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="grid lg:grid-cols-12 gap-5 mt-14">
          <div className="lg:col-span-8 space-y-8">
            <div>
              <h2 className="font-display text-3xl font-black uppercase mb-5">In-Play & Upcoming</h2>
              <div className="bg-zinc-950 border border-amber-500/20 rounded-md overflow-hidden">
                {matches.length === 0 && <div className="p-6 text-zinc-500">No open markets at the moment.</div>}
                {matches.map((m) => <MatchRow key={m.id} m={m} onPick={setPick} />)}
              </div>
            </div>
            <MyBets reloadKey={reloadKey} />
          </div>
          <div className="lg:col-span-4">
            <BetSlipDock pick={pick} onClear={() => setPick(null)} onPlaced={() => setReloadKey((k) => k + 1)} />
          </div>
        </div>
      </div>
      <WhatsAppButton />
    </div>
  );
}
